import { useCallback, useMemo, useState } from "react";
import { OrganizationTaskTemplate } from "@common/types/organizationTaskTemplate";
import { FiltersTypes } from "@common/types/filters/general";
import { OrganizationTaskTemplatesView } from "@common/types/views/organizationTaskTemplatesView";
import { View, ViewOptions } from "@common/types/views/views";
import { Loader } from "@design-system/Loader/Loader";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import {
    useOrganizationTaskTemplates,
    useOrganizationTaskTemplateServiceTemplateCounts,
} from "@api/organizationTaskTemplate.api";
import { useSortedCloseStages } from "@api/organizationCloseStage.api";
import { OrganizationTaskTemplateStatusFilter } from "@common/schemas/organization-task-template.schema";
import { ArchiveFilter } from "../templates/archiveFilter";
import { isTaskTypeValue } from "@components/pageHeader/filters/Tabs/OrgTaskTemplateFilters/orgTaskTemplateFilterOptions";
import { EditOrganizationTaskTemplateDrawer } from "./EditOrganizationTaskTemplateDrawer";
import { OrganizationTaskTemplatesEmptyState } from "./OrganizationTaskTemplatesEmptyState";
import { OrganizationTaskTemplateServiceTemplatesDrawer } from "./OrganizationTaskTemplateServiceTemplatesDrawer";
import { OrganizationTaskTemplatesFooter } from "./OrganizationTaskTemplatesFooter";
import type { TaskTypeOption } from "../OrganizationCloseTemplates/closeTemplateTasksDrawer.constants";
import { filterKickDefaultTemplateRows } from "./kickDefaultTaskTemplates";
import { toTemplateRow } from "./organizationTaskTemplateMappers";
import { OrganizationTaskTemplatesTableMeta } from "./organizationTaskTemplatesTableMeta";
import { organizationTaskTemplatesViewConfig } from "./organizationTaskTemplatesViewConfig";
import {
    OrganizationTaskTemplateSortValue,
    parseTemplateSort,
} from "./organizationTaskTemplatesSort";
import { useOrganizationTaskTemplateBulkActions } from "./useOrganizationTaskTemplateBulkActions";
import { useOrganizationTaskTemplateInlineUpdates } from "./useOrganizationTaskTemplateInlineUpdates";
import { useOrganizationTaskTemplateLifecycle } from "./useOrganizationTaskTemplateLifecycle";
import { useOrganizationTaskTemplateRowSelection } from "./useOrganizationTaskTemplateRowSelection";

const BULK_SELECTION_VIEW_OPTIONS = {
    selection: true,
    bulkActions: true,
} satisfies ViewOptions;

const STATUS_BY_ARCHIVE_FILTER: Record<
    ArchiveFilter,
    OrganizationTaskTemplateStatusFilter
> = {
    active: OrganizationTaskTemplateStatusFilter.ACTIVE,
    archived: OrganizationTaskTemplateStatusFilter.ARCHIVED,
    all: OrganizationTaskTemplateStatusFilter.ALL,
};

interface Props {
    search: string;
    archiveFilter: ArchiveFilter;
    filters: FiltersTypes;
    sort: OrganizationTaskTemplateSortValue;
    onSortChange: (sort: OrganizationTaskTemplateSortValue) => void;
    onSortRemove?: () => void;
    view: OrganizationTaskTemplatesView;
    onViewChange: (view: View) => void;
    hasActiveFilters: boolean;
    onClearFilters: () => void;
    onCreateTemplate: () => void;
    onOverrideKickDefault: (taskTypeOption: TaskTypeOption) => void;
}

export const OrganizationTaskTemplatesList = ({
    search,
    archiveFilter,
    filters,
    sort,
    onSortChange,
    onSortRemove,
    view,
    onViewChange,
    hasActiveFilters,
    onClearFilters,
    onCreateTemplate,
    onOverrideKickDefault,
}: Props) => {
    const listOptions = useMemo(
        () => ({
            status: STATUS_BY_ARCHIVE_FILTER[archiveFilter],
            types: filters.orgTaskTemplateTypes?.filter(isTaskTypeValue),
            groupIds: filters.orgTaskTemplateGroupIds,
            assigneeRoles: filters.orgTaskTemplateAssigneeRoles,
            createdByUserIds: filters.orgTaskTemplateCreatedByUserIds,
            ...parseTemplateSort(sort),
        }),
        [archiveFilter, filters, sort],
    );
    const {
        data: templates,
        isPending,
        isError,
        isFetching,
        refetch,
    } = useOrganizationTaskTemplates(listOptions);
    const { data: serviceTemplateCounts } =
        useOrganizationTaskTemplateServiceTemplateCounts(listOptions);
    const sortedStages = useSortedCloseStages();
    const [editingTemplateId, setEditingTemplateId] = useState<string | null>(
        null,
    );

    const [serviceTemplatesTaskTemplateId, setServiceTemplatesTaskTemplateId] =
        useState<string | null>(null);

    const templatesList = useMemo(() => templates ?? [], [templates]);
    const editingTemplate = useMemo(
        () => templatesList.find((t) => t.id === editingTemplateId) ?? null,
        [templatesList, editingTemplateId],
    );

    const rows = useMemo(() => {
        const lowerSearch = search.toLowerCase();
        const templateRows = templatesList
            .filter(
                (t) => !search || t.title.toLowerCase().includes(lowerSearch),
            )
            .map(toTemplateRow);
        if (isError) {
            return templateRows;
        }
        return [
            ...templateRows,
            ...filterKickDefaultTemplateRows({
                search,
                archiveFilter,
                types: listOptions.types,
                groupIds: listOptions.groupIds,
                assigneeRoles: listOptions.assigneeRoles,
                createdByUserIds: listOptions.createdByUserIds,
            }),
        ];
    }, [templatesList, search, isError, archiveFilter, listOptions]);

    const templatesById = useMemo(
        () => new Map(templatesList.map((t) => [t.id, t])),
        [templatesList],
    );

    const { rowSelection, setRowSelection, selectedIds, clearSelection } =
        useOrganizationTaskTemplateRowSelection(rows);
    const selectedTemplates = useMemo(
        () =>
            selectedIds
                .map((id) => templatesById.get(id))
                .filter((t): t is OrganizationTaskTemplate => !!t),
        [selectedIds, templatesById],
    );
    const {
        isBusy: isBulkBusy,
        handleOpenBulkActions,
        bulkModals,
    } = useOrganizationTaskTemplateBulkActions({
        selectedTemplates,
        serviceTemplateCounts: serviceTemplateCounts ?? {},
        onClearSelection: clearSelection,
    });

    const viewWithBulkActions = useMemo<OrganizationTaskTemplatesView>(
        () => ({
            ...view,
            options: { ...view.options, ...BULK_SELECTION_VIEW_OPTIONS },
        }),
        [view],
    );

    const handleViewChange = useCallback(
        (newView: View) => {
            const { selection, bulkActions, ...persistedOptions } =
                newView.options ?? {};
            onViewChange({ ...newView, options: persistedOptions });
        },
        [onViewChange],
    );

    const inlineUpdates = useOrganizationTaskTemplateInlineUpdates();
    const {
        handleArchiveTemplate,
        handleUnarchiveTemplate,
        handleDetachTemplate,
        handleDeleteTemplate,
        lifecycleModals,
    } = useOrganizationTaskTemplateLifecycle({ templates: templatesList });

    const handleCloseEditDrawer = useCallback(
        () => setEditingTemplateId(null),
        [],
    );

    const handleCloseServiceTemplatesDrawer = useCallback(
        () => setServiceTemplatesTaskTemplateId(null),
        [],
    );

    const tableMeta = useMemo<OrganizationTaskTemplatesTableMeta>(
        () => ({
            callbacks: {
                ...inlineUpdates,
                onEditTask: setEditingTemplateId,
                onArchiveTemplate: handleArchiveTemplate,
                onUnarchiveTemplate: handleUnarchiveTemplate,
                onDetachTemplate: handleDetachTemplate,
                onDeleteTemplate: handleDeleteTemplate,
                onViewServiceTemplates: setServiceTemplatesTaskTemplateId,
                onOverrideKickDefault,
            },
            sortedStages,
            templatesById,
            serviceTemplateCounts: serviceTemplateCounts ?? {},
        }),
        [
            inlineUpdates,
            handleArchiveTemplate,
            handleUnarchiveTemplate,
            handleDetachTemplate,
            handleDeleteTemplate,
            onOverrideKickDefault,
            sortedStages,
            templatesById,
            serviceTemplateCounts,
        ],
    );

    return (
        <>
            <ViewTable
                data={isPending ? null : rows}
                view={viewWithBulkActions}
                config={organizationTaskTemplatesViewConfig}
                onViewChange={handleViewChange}
                sort={sort}
                onSortChange={onSortChange}
                onSortRemove={onSortRemove}
                isSortLoading={isFetching}
                rowSelection={rowSelection}
                onRowSelectionChange={setRowSelection}
                emptyView={() => (
                    <OrganizationTaskTemplatesEmptyState
                        isError={isError}
                        hasActiveFilters={hasActiveFilters}
                        onRetry={() => void refetch()}
                        onClearFilters={onClearFilters}
                        onCreateTemplate={onCreateTemplate}
                    />
                )}
                loadingView={() => <Loader />}
                tableMeta={tableMeta}
                fixedTopOffsetPx
                enableColumnResizing
                enableRowSelection={(row) =>
                    row.kickDefaultOption === undefined
                }
                onRowClick={(row) => {
                    if (!row) {
                        return;
                    }
                    if (row.kickDefaultOption !== undefined) {
                        onOverrideKickDefault(row.kickDefaultOption);
                        return;
                    }
                    setEditingTemplateId(row.id);
                }}
                footer={
                    rows.length > 0 ? (
                        <OrganizationTaskTemplatesFooter
                            total={rows.length}
                            selectedCount={selectedIds.length}
                            isBusy={isBulkBusy}
                            onClearSelection={clearSelection}
                            onOpenBulkActions={handleOpenBulkActions}
                        />
                    ) : undefined
                }
            />

            <EditOrganizationTaskTemplateDrawer
                template={editingTemplate}
                onClose={handleCloseEditDrawer}
            />

            <OrganizationTaskTemplateServiceTemplatesDrawer
                taskTemplateId={serviceTemplatesTaskTemplateId}
                taskTemplateName={
                    serviceTemplatesTaskTemplateId
                        ? (templatesById.get(serviceTemplatesTaskTemplateId)
                              ?.title ?? null)
                        : null
                }
                onClose={handleCloseServiceTemplatesDrawer}
            />

            {lifecycleModals}
            {bulkModals}
        </>
    );
};
